import React, { useState, useMemo } from "react";
import "./CalendarioAutomezzi.css";
import { useCollection } from "../../hooks/useCollection";
import Select from "react-select";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { database, timestamp } from "../../firebase/config";
import { startOfDay, endOfDay } from "date-fns";

export default function CalendarioAutomezzi() {
  // Stati per la ricerca
  const [selectedMezzo, setSelectedMezzo] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [searchResults, setSearchResults] = useState(null);

  // Caricamento dati di supporto
  const { documents: automezziData, error: automezziError } =
    useCollection("automezzi");
  const { documents: usersData, error: usersError } = useCollection("users");

  // Funzione per ottenere il nome utente
  const getUserName = (userId) => {
    if (!usersData) return "Caricamento...";

    const user = usersData.find((u) => u.id === userId);
    if (!user) return "Utente non trovato";

    if (user.anagrafica && user.anagrafica.nome && user.anagrafica.cognome) {
      return `${user.anagrafica.nome} ${user.anagrafica.cognome}`;
    }

    if (user.displayName) {
      if (typeof user.displayName === "object" && user.displayName.nome) {
        return user.displayName.nome;
      }
      if (typeof user.displayName === "string") {
        return user.displayName;
      }
    }

    return user.email || "Nome non disponibile";
  };

  // Opzioni per il select dei mezzi
  const mezziOptions = useMemo(() => {
    if (!automezziData) return [];

    return automezziData.map((automezzo) => ({
      value: automezzo.modello,
      label: `${automezzo.modello} - ${automezzo.targa}`,
    }));
  }, [automezziData]);

  // Funzione di ricerca (query lato Firestore, filtrata per giorno selezionato)
  const handleSearch = async () => {
    if (!selectedMezzo || !selectedDate) {
      alert("Seleziona un mezzo e una data per effettuare la ricerca");
      return;
    }

    try {
      setHasSearched(false);
      setSearchResults(null);

      const dayStart = startOfDay(new Date(selectedDate));
      const dayEnd = endOfDay(new Date(selectedDate));

      const snapshot = await database
        .collection("presenze")
        .where("dataRegistrazione", ">=", timestamp.fromDate(dayStart))
        .where("dataRegistrazione", "<=", timestamp.fromDate(dayEnd))
        .orderBy("dataRegistrazione", "asc")
        .get();

      const dayPresenze = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const filteredPresenze =
        dayPresenze.filter((presenza) => {
          const modello = presenza?.mezzo?.modello;
          return modello && modello === selectedMezzo.value;
        }) || [];

      // Prepara i risultati
      const results = {
        mezzo: selectedMezzo,
        data: selectedDate,
        presenze: filteredPresenze.map((presenza) => ({
          userId: presenza.userId,
          userName: getUserName(presenza.userId),
          cantiere:
            presenza.cantiere?.nome || presenza.cantiere || "Non specificato",
          orarioIngresso: presenza.orarioIngresso,
          orarioUscita: presenza.orarioUscita,
          note: presenza.note,
        })),
      };

      setSearchResults(results);
      setHasSearched(true);
    } catch (err) {
      console.error("Errore ricerca presenze per mezzo/data:", err);
      alert("Errore durante la ricerca. Riprova più tardi.");
    }
  };

  // Reset della ricerca
  const handleReset = () => {
    setSelectedMezzo(null);
    setSelectedDate("");
    setHasSearched(false);
    setSearchResults(null);
  };

  return (
    <div className="calendario-automezzi">
      <div className="page-header">
        <h1>Calendario Automezzi</h1>
        <p>Cerca l'utilizzo di un mezzo specifico in una data specifica</p>
      </div>

      {/* Form di ricerca */}
      <div className="search-form">
        <div className="search-controls">
          <div className="form-group">
            <label htmlFor="mezzo-select">Seleziona Mezzo:</label>
            <Select
              id="mezzo-select"
              value={selectedMezzo}
              onChange={setSelectedMezzo}
              options={mezziOptions}
              className="react-select-container"
              classNamePrefix="react-select"
              placeholder="Seleziona un mezzo..."
              isClearable
              isSearchable
            />
          </div>

          <div className="form-group">
            <label htmlFor="date-picker">Seleziona Data:</label>
            <input
              id="date-picker"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="date-input"
            />
          </div>

          <div className="search-buttons">
            <button
              onClick={handleSearch}
              className="btn-search"
              disabled={!selectedMezzo || !selectedDate}
            >
              Cerca
            </button>
            <button onClick={handleReset} className="btn-reset">
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Risultati della ricerca */}
      {hasSearched && (
        <div className="search-results">
          {searchResults && (
            <>
              <div className="results-header">
                <h2>Risultati della Ricerca</h2>
                <div className="search-info">
                  <span>
                    <strong>Mezzo:</strong> {searchResults.mezzo.label}
                  </span>
                  <span>
                    <strong>Data:</strong>{" "}
                    {format(new Date(searchResults.data), "dd/MM/yyyy", {
                      locale: it,
                    })}
                  </span>
                </div>
              </div>

              {searchResults.presenze.length === 0 ? (
                <div className="no-results">
                  <p>
                    Nessun utilizzo trovato per il mezzo selezionato nella data
                    specificata.
                  </p>
                </div>
              ) : (
                <div className="results-list">
                  <h3>Utilizzi trovati ({searchResults.presenze.length})</h3>

                  {searchResults.presenze.map((utilizzo, index) => (
                    <div key={index} className="utilizzo-card">
                      <div className="utilizzo-header">
                        <strong>{utilizzo.userName}</strong>
                        {utilizzo.cantiere &&
                          utilizzo.cantiere !== "Non specificato" && (
                            <span className="cantiere-badge">
                              {utilizzo.cantiere}
                            </span>
                          )}
                      </div>

                      <div className="utilizzo-details">
                        <div className="orari">
                          {utilizzo.orarioIngresso && (
                            <span className="orario-item">
                              <strong>Ingresso:</strong>{" "}
                              {utilizzo.orarioIngresso}
                            </span>
                          )}
                          {utilizzo.orarioUscita && (
                            <span className="orario-item">
                              <strong>Uscita:</strong> {utilizzo.orarioUscita}
                            </span>
                          )}
                        </div>

                        {utilizzo.note && (
                          <div className="note">
                            <strong>Note:</strong> {utilizzo.note}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Messaggio iniziale */}
      {!hasSearched && (
        <div className="welcome-message">
          <div className="welcome-content">
            <h3>👋 Benvenuto nel Calendario Automezzi</h3>
            <p>
              Seleziona un mezzo e una data per visualizzare chi ha utilizzato
              il veicolo in quel giorno specifico.
            </p>
            <ul>
              <li>🚗 Scegli il mezzo dall'elenco</li>
              <li>📅 Seleziona la data di interesse</li>
              <li>🔍 Clicca "Cerca" per vedere i risultati</li>
            </ul>
          </div>
        </div>
      )}

      {/* Gestione errori */}
      {(automezziError || usersError) && (
        <div className="error-message">
          <p>Errore nel caricamento dei dati:</p>
          {automezziError && <p>- Automezzi: {automezziError}</p>}
          {usersError && <p>- Utenti: {usersError}</p>}
        </div>
      )}
    </div>
  );
}
